import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/format";
import { Compass } from "@/components/Compass";
import { BraveBadge } from "@/components/BraveBadge";
import { GeniusBadge } from "@/components/GeniusBadge";
import { BagButton } from "@/components/Bag";
import { ReviewsModal } from "@/components/ReviewsModal";
import { CompletedUndertakings } from "@/components/CompletedUndertakings";
import { MemberPosts } from "@/components/MemberPosts";
import { Check } from "lucide-react";
import { toast } from "sonner";

const XP_SLOTS = 10;
const padXp = (arr) => {
  const list = Array.isArray(arr) ? arr.slice(0, XP_SLOTS).map((e) => ({ title: e.title || "", description: e.description || "", verified: !!e.verified })) : [];
  while (list.length < XP_SLOTS) list.push({ title: "", description: "", verified: false });
  return list;
};

export function ProfileDialog({ userId, onClose, noReview = false }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [openXp, setOpenXp] = useState(null);
  const [verifyIndex, setVerifyIndex] = useState(null);
  const [verifyErr, setVerifyErr] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateBusy, setTerminateBusy] = useState(false);

  const isSelf = user?.id === userId;
  const canTerminate = user?.is_god && profile && !profile.is_god && !isSelf;

  const submitTerminate = async () => {
    if (terminateBusy) return;
    setTerminateBusy(true);
    try {
      await api.delete(`/users/${userId}`);
      toast.success("account terminated");
      setTerminateOpen(false);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTerminateBusy(false);
    }
  };

  const load = useCallback(() => {
    if (!userId) return;
    api.get(`/users/${userId}`).then(({ data }) => setProfile(data));
  }, [userId]);

  const submitVerify = async () => {
    if (verifyBusy) return;
    setVerifyBusy(true);
    setVerifyErr("");
    try {
      await api.post(`/users/${userId}/verify-xp`, { index: verifyIndex });
      setVerifyIndex(null);
      load();
    } catch (err) {
      setVerifyErr("could not update");
    } finally {
      setVerifyBusy(false);
    }
  };

  useEffect(() => {
    setProfile(null);
    setReviewsOpen(false);
    setOpenXp(null);
    load();
  }, [userId, load]);

  if (!userId) return null;

  const xp = padXp(profile?.natural_xp);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="profile-dialog-overlay"
    >
      <div
        className="w-full max-w-2xl p-8 my-auto"
        style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="profile-dialog"
      >
        <div className="flex justify-between items-start mb-8">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
            Profile
          </span>
          <button
            onClick={onClose}
            className="text-sm hover:opacity-100 opacity-60 transition-opacity"
            style={{ color: "var(--text-primary)" }}
            data-testid="profile-dialog-close"
          >
            [ close ]
          </button>
        </div>

        {profile ? (
          <div>
            {/* Username + average rating */}
            <div className="flex items-baseline justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="font-head text-2xl md:text-3xl break-words" style={{ color: "var(--text-primary)" }} data-testid="profile-username-display">
                  {profile.name || "Unnamed"}
                </h2>
                {profile?.is_genius && <GeniusBadge />}
                {profile?.is_brave && <BraveBadge />}
              </div>
              <span className="font-head text-2xl md:text-3xl shrink-0" style={{ color: "var(--text-primary)" }} data-testid="profile-avg-rating">
                {profile.is_god ? <span style={{ fontSize: "1.5em", lineHeight: 1, verticalAlign: "-0.12em" }}>∞</span> : (profile.average_rating != null ? `${profile.average_rating.toFixed(2)}/10` : "—")}
              </span>
            </div>

            {/* Stats + compass + country/team */}
            <div className="flex items-center gap-6 sm:gap-8 mb-12">
              <div>
                <p className="text-base" style={{ color: "var(--text-secondary)" }} data-testid="profile-prompt-count">
                  {profile.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : profile.prompt_count} asks
                </p>
                <p className="text-base mb-4" style={{ color: "var(--text-secondary)" }} data-testid="profile-response-count">
                  {profile.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : profile.response_count} answers
                </p>
                <button onClick={() => setReviewsOpen(true)} className="btn text-xs tracking-widest px-4 py-2" data-testid="profile-review-summary">
                  REVIEWS · {profile.is_god ? <span style={{ fontSize: "1.6em", lineHeight: 1, verticalAlign: "-0.15em" }}>∞</span> : (profile.review_count || 0)}
                </button>
                <div className="mt-3"><BagButton amount={profile.provisions} isGod={profile.is_god} testId="profile-dialog-bag-btn" /></div>
              </div>
              <Compass value={profile.compass || ""} />
              <div className="min-w-0 flex-1 self-stretch flex flex-col justify-between gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Country</label>
                  <p className="py-1 mt-1 text-base break-words" style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--border)" }} data-testid="profile-location">
                    {profile.location || "—"}
                  </p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>walk</label>
                  <p className="py-1 mt-1 text-base" style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--border)" }} data-testid="profile-team">
                    {profile.team || "—"}
                  </p>
                </div>
              </div>
            </div>

            {profile.is_god ? (
              <div className="mb-10"><MemberPosts userId={userId} canPost={user?.id === userId} /></div>
            ) : (
            <div className="mb-10">
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Natural XP</label>
              <div className="flex flex-col gap-3 mt-3" data-testid="natural-xp-grid">
                {xp.map((e, i) => {
                  const canVerify = user?.is_god && profile && !profile.is_god;
                  return (
                    <div key={i} className="flex items-stretch gap-3">
                      <button
                        onClick={() => e.description && setOpenXp(i)}
                        data-testid={`natural-xp-slot-${i}`}
                        className="btn flex-1 min-w-0 text-left px-4 py-3 text-sm truncate"
                        style={!e.title ? { color: "var(--text-muted)" } : undefined}
                      >
                        {e.title || `entry ${i + 1}`}
                      </button>
                      <button
                        data-testid={`natural-xp-verify-${i}`}
                        onClick={canVerify ? () => { setVerifyErr(""); setVerifyIndex(i); } : undefined}
                        disabled={!canVerify}
                        title={canVerify ? "verify this entry" : undefined}
                        className={`shrink-0 flex items-center justify-center w-[46px] transition-colors duration-200 ${canVerify ? "hover:opacity-100 opacity-80 cursor-pointer" : "cursor-default"}`}
                        style={{ border: "1px solid var(--border)", color: (e.verified || profile?.is_god) ? "var(--accent)" : "#5A5A5A" }}
                      >
                        <Check size={18} strokeWidth={2} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {!profile.is_god && <div className="mt-8"><CompletedUndertakings userId={userId} /></div>}

            {canTerminate && (
              <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
                <button
                  onClick={() => setTerminateOpen(true)}
                  data-testid="profile-terminate-btn"
                  className="btn w-full py-3 text-sm tracking-widest"
                >
                  TERMINATE ACCOUNT
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
        )}

        {/* Nested XP entry popup — must be closed before the profile dialog */}
        {openXp != null && xp[openXp]?.description && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in"
            style={{ background: "rgba(0,0,0,0.7)" }}
            onClick={() => setOpenXp(null)}
            data-testid="natural-xp-detail-overlay"
          >
            <div
              className="w-full max-w-md p-8"
              style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="natural-xp-detail-modal"
            >
              <div className="flex justify-between items-start gap-4 mb-6">
                <h3 className="font-head text-lg break-words" style={{ color: "var(--text-primary)" }} data-testid="natural-xp-detail-title">
                  {xp[openXp].title || `entry ${openXp + 1}`}
                </h3>
                <button
                  onClick={() => setOpenXp(null)}
                  data-testid="natural-xp-detail-close"
                  className="text-sm opacity-60 hover:opacity-100 shrink-0"
                  style={{ color: "var(--text-primary)" }}
                >
                  [ close ]
                </button>
              </div>
              <p className="long-text-sm text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid="natural-xp-detail-desc">
                {xp[openXp].description}
              </p>
            </div>
          </div>
        )}

        {/* God-only manual verification password gate */}
        {verifyIndex != null && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in"
            style={{ background: "rgba(0,0,0,0.7)" }}
            onClick={() => setVerifyIndex(null)}
            data-testid="verify-xp-overlay"
          >
            <div
              className="w-full max-w-sm p-8"
              style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="verify-xp-modal"
            >
              <div className="flex justify-between items-start mb-6">
                <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                  {xp[verifyIndex]?.verified ? "unverify entry" : "verify entry"}
                </span>
                <button onClick={() => setVerifyIndex(null)} data-testid="verify-xp-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
              </div>
              {verifyErr && <p className="text-xs mb-3" style={{ color: "var(--accent)" }} data-testid="verify-xp-error">{verifyErr}</p>}
              <button onClick={submitVerify} disabled={verifyBusy} data-testid="verify-xp-submit" className="btn w-full py-3 text-sm tracking-widest">
                {verifyBusy ? "…" : "CONFIRM"}
              </button>
            </div>
          </div>
        )}

        {/* God-only termination confirm */}
        {terminateOpen && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-6 fade-in"
            style={{ background: "rgba(0,0,0,0.8)" }}
            onClick={() => setTerminateOpen(false)}
            data-testid="terminate-overlay"
          >
            <div
              className="w-full max-w-md p-8"
              style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="terminate-modal"
            >
              <p className="text-base mb-8" style={{ color: "var(--text-secondary)" }} data-testid="terminate-text">
                permanently terminate {profile?.name || "this user"}? all their asks, answers and reviews will be wiped and the username freed. this cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setTerminateOpen(false)} data-testid="terminate-cancel" className="btn flex-1 py-3 text-sm tracking-widest">CANCEL</button>
                <button onClick={submitTerminate} disabled={terminateBusy} data-testid="terminate-confirm" className="btn flex-1 py-3 text-sm tracking-widest">{terminateBusy ? "…" : "TERMINATE"}</button>
              </div>
            </div>
          </div>
        )}
        {reviewsOpen && <ReviewsModal userId={userId} noReview={noReview} onClose={() => { setReviewsOpen(false); load(); }} />}
      </div>
    </div>
  );
}
